/**
 * One drawer of Modula 1, drawn to scale for «Dove lo metto» (rule 47-ter).
 *
 * Pure: everything comes from props, and a tap only says *where* it landed.
 * What a tap means (select a choice, or move it there) is the dialog's call.
 *
 * Geometry is the Modula's, in millimetres: the drawer is 4100×857, X 0 is the
 * left side as seen from the bay and Y 0 is the back, far from the operator.
 * The SVG keeps those units in its `viewBox`, so the back row is on top and the
 * drawing scales to whatever width the dialog has — no horizontal scroll on a
 * tablet. The strip boxes (410×610) on the right are drawn by their own
 * coordinates like any other box.
 */

import { useId, type KeyboardEvent } from 'react';
import { roomFor } from '@errebi/domain';
import type { AssegnazionePosto, EsitoPosti } from '@errebi/shared';

type Proposta = Extract<EsitoPosti, { esito: 'proposta' }>;
export type DrawerBox = Proposta['boxes'][number];
export type DrawerSlot = Proposta['slots'][number];
export type Facts = Proposta['facts'];
export type PlaceTarget = AssegnazionePosto['target'];

export const DRAWER_WIDTH = 4100;
export const DRAWER_DEPTH = 857;

/** Text size and line height in drawer millimetres: about 15px on a tablet. */
const TEXT = 62;
const LINE = 74;
const PAD = 30;
/** The border inset of a drawn box, on each side. */
const INSET = 6;
/**
 * Width of one character of the monospace labels, in millimetres: 0.6 em is
 * the advance of ui-monospace / Menlo / Consolas. Conservative on purpose: a
 * label one letter too short is fine, one letter too long sits on the next box.
 */
const CHAR = TEXT * 0.6;

/** How many label characters fit across a box this wide. */
export function maxCharsFor(width: number): number {
  return Math.max(4, Math.floor((width - 2 * INSET - 2 * PAD) / CHAR));
}

/**
 * `name + suffix` cut to `max` characters: the suffix (the pieces) always
 * stays, the name loses its end and gets «…».
 */
export function fitLabel(name: string, suffix: string, max: number): string {
  if (name.length + suffix.length <= max) return name + suffix;
  const room = max - suffix.length - 1;
  return room <= 0 ? (name + suffix).slice(0, max) : `${name.slice(0, room)}…${suffix}`;
}

/** How many label lines fit in a box this deep, keeping `reserved` lines free. */
const maxLinesFor = (depth: number, reserved: number): number =>
  Math.max(1, Math.floor((depth - 2 * INSET - 2 * PAD) / LINE) - reserved);

export interface DrawerDrawingProps {
  readonly drawer: number;
  readonly boxes: readonly DrawerBox[];
  readonly slots: readonly DrawerSlot[];
  /** The current choices that land in this drawer. */
  readonly assignments: readonly AssegnazionePosto[];
  readonly facts: Facts;
  /** Pieces per box by product id. */
  readonly capacities: Readonly<Record<string, number>>;
  /** The choice the operator tapped, if any: its product is the one the room is told for. */
  readonly selected: string | null;
  /** A tap on a box or a free slot, with the choices already there. */
  readonly onTap: (target: PlaceTarget, here: readonly string[]) => void;
}

const sameSpot = (t: PlaceTarget, a: { drawer: number; x: number; y: number }): boolean =>
  t.drawer === a.drawer && t.x === a.x && t.y === a.y;

/** «L Black · 12», or the article code when its size and colour are unknown. */
export function shortLabel(article: string, pieces: number, facts: Facts): string {
  return labelParts(article, pieces, facts).join('');
}

function labelParts(article: string, pieces: number, facts: Facts): [string, string] {
  const f = facts[article];
  return [f ? `${f.size} ${f.color}` : article, ` · ${pieces}`];
}

/** One label line for a box of this width. */
const lineFor = (
  prefix: string,
  article: string,
  pieces: number,
  facts: Facts,
  width: number,
): string => {
  const [name, suffix] = labelParts(article, pieces, facts);
  return fitLabel(prefix + name, suffix, maxCharsFor(width));
};

/** Fraction of a box taken by these contents; null when a capacity is unknown. */
function fillOf(
  contents: readonly { article: string; pieces: number }[],
  facts: Facts,
  capacities: Readonly<Record<string, number>>,
): number | null {
  let fill = 0;
  for (const c of contents) {
    const f = facts[c.article];
    const cap = f ? capacities[String(f.productId)] : undefined;
    if (cap === undefined || cap <= 0) return null;
    fill += c.pieces / cap;
  }
  return fill;
}

export function DrawerDrawing({
  drawer,
  boxes,
  slots,
  assignments,
  facts,
  capacities,
  selected,
  onTap,
}: DrawerDrawingProps) {
  // Each box clips its own text: two drawings on one page must not share ids.
  const uid = useId().replace(/:/g, '');
  const clipId = (kind: string, x: number, y: number) => `ritaglio-${uid}-${kind}-${x}-${y}`;
  const chosen = assignments.find((a) => a.id === selected) ?? assignments[0] ?? null;
  const chosenFact = chosen ? facts[chosen.article] : undefined;
  const chosenCap = chosenFact ? capacities[String(chosenFact.productId)] : undefined;

  const into = (kind: PlaceTarget['kind'], spot: { x: number; y: number }) =>
    assignments.filter((a) => a.target.kind === kind && sameSpot(a.target, { drawer, ...spot }));

  const key = (e: KeyboardEvent, run: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      run();
    }
  };

  return (
    <figure className="posto__cassetto" role="group" aria-label={`Cassetto ${drawer}`}>
      <figcaption className="posto__didascalia">
        <b>Cassetto {drawer}</b>
        <span className="mod__hint">in alto il fondo, in basso il tuo lato</span>
      </figcaption>
      <svg
        className="posto__disegno"
        viewBox={`0 0 ${DRAWER_WIDTH} ${DRAWER_DEPTH}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <rect className="posto__fondo" x={0} y={0} width={DRAWER_WIDTH} height={DRAWER_DEPTH} />

        {boxes.map((b) => {
          const spot = { x: b.x, y: b.y };
          const rect = (
            <rect
              className="posto__bordo"
              x={b.x + INSET}
              y={b.y + INSET}
              width={b.width - 2 * INSET}
              height={b.depth - 2 * INSET}
            />
          );
          if (b.container === 'other') {
            return (
              <g key={`b${b.x}:${b.y}`} className="posto__altro" aria-hidden="true">
                {rect}
              </g>
            );
          }
          const arriving = into('box', spot);
          const after = [...b.contents, ...arriving];
          const fill = fillOf(after, facts, capacities);
          const room = fill !== null && chosenCap !== undefined ? roomFor(fill, chosenCap) : null;
          const contents = b.contents.map((c) => shortLabel(c.article, c.pieces, facts));
          const name = b.group ?? `${drawer} · x ${b.x} · y ${b.y}`;
          const label =
            `Scatola ${name}: ${contents.join(', ') || 'vuota'}` +
            (arriving.length > 0
              ? `; in arrivo ${arriving.map((a) => `${a.pieces} pezzi di ${a.article}`).join(', ')}`
              : '') +
            (room !== null ? `; posto per ${room}` : '');
          const all = [
            ...b.contents.map((c) => lineFor('', c.article, c.pieces, facts, b.width)),
            ...arriving.map((a) => lineFor('+ ', a.article, a.pieces, facts, b.width)),
          ];
          const fit = maxLinesFor(b.depth, room !== null ? 1 : 0);
          const lines =
            all.length <= fit
              ? all
              : [
                  ...all.slice(0, fit - 1),
                  fitLabel(`+${all.length - fit + 1} altri`, '', maxCharsFor(b.width)),
                ];
          const clip = clipId('b', b.x, b.y);
          const tap = () =>
            onTap(
              { kind: 'box', drawer, ...spot },
              arriving.map((a) => a.id),
            );
          return (
            <g
              key={`b${b.x}:${b.y}`}
              role="button"
              tabIndex={0}
              aria-label={label}
              aria-pressed={arriving.some((a) => a.id === selected)}
              className={
                'posto__scatola posto__scatola--esistente' +
                (arriving.length > 0 ? ' posto__scatola--riceve' : '')
              }
              onClick={tap}
              onKeyDown={(e) => key(e, tap)}
            >
              {rect}
              <clipPath id={clip}>{rect}</clipPath>
              <g clipPath={`url(#${clip})`}>
                {lines.map((t, i) => (
                  <text key={i} x={b.x + PAD} y={b.y + PAD + LINE * (i + 1)} fontSize={TEXT}>
                    {t}
                  </text>
                ))}
                {room !== null && (
                  <text
                    className="posto__resta"
                    x={b.x + PAD}
                    y={b.y + b.depth - PAD}
                    fontSize={TEXT}
                  >
                    {fitLabel(`posto per ${room}`, '', maxCharsFor(b.width))}
                  </text>
                )}
              </g>
            </g>
          );
        })}

        {slots.map((s) => {
          const spot = { x: s.x, y: s.y };
          const arriving = into('new', spot);
          const tap = () =>
            onTap(
              { kind: 'new', drawer, ...spot },
              arriving.map((a) => a.id),
            );
          const rect = (
            <rect
              className="posto__bordo"
              x={s.x + INSET}
              y={s.y + INSET}
              width={s.width - 2 * INSET}
              height={s.depth - 2 * INSET}
            />
          );
          if (arriving.length === 0) {
            return (
              <g
                key={`s${s.x}:${s.y}`}
                role="button"
                tabIndex={0}
                aria-label={`Posto libero ${drawer} · x ${s.x} · y ${s.y}`}
                className="posto__libero"
                onClick={tap}
                onKeyDown={(e) => key(e, tap)}
              >
                {rect}
              </g>
            );
          }
          const clip = clipId('s', s.x, s.y);
          const all = arriving.map((a) => lineFor('', a.article, a.pieces, facts, s.width));
          const fit = maxLinesFor(s.depth, 1);
          const lines =
            all.length <= fit
              ? all
              : [
                  ...all.slice(0, fit - 1),
                  fitLabel(`+${all.length - fit + 1} altri`, '', maxCharsFor(s.width)),
                ];
          const label = `Scatola nuova: ${arriving
            .map((a) => {
              const f = facts[a.article];
              return `${a.pieces} pezzi di ${a.article}${f ? ` (${f.size} ${f.color})` : ''}`;
            })
            .join(', ')}`;
          return (
            <g
              key={`s${s.x}:${s.y}`}
              role="button"
              tabIndex={0}
              aria-label={label}
              aria-pressed={arriving.some((a) => a.id === selected)}
              className="posto__scatola posto__scatola--proposta"
              onClick={tap}
              onKeyDown={(e) => key(e, tap)}
            >
              {rect}
              <clipPath id={clip}>{rect}</clipPath>
              <g clipPath={`url(#${clip})`}>
                <text x={s.x + PAD} y={s.y + PAD + LINE} fontSize={TEXT}>
                  nuova
                </text>
                {lines.map((t, i) => (
                  <text key={i} x={s.x + PAD} y={s.y + PAD + LINE * (i + 2)} fontSize={TEXT}>
                    {t}
                  </text>
                ))}
              </g>
            </g>
          );
        })}
      </svg>
    </figure>
  );
}
